'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';


interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  is_read: boolean;
  reference_id: string | null;
  reference_type: string | null;
  created_at: string;
}

type FilterType = 'all' | 'unread' | 'bid' | 'delivery' | 'payment' | 'dispute' | 'moderator' | 'message';

const TYPE_CONFIG: Record<string, { icon: string; color: string; label: string; category: FilterType }> = {
  order_created:     { label: 'Order Created',         icon: '📋', color: 'text-purple-600 bg-purple-100',   category: 'bid' },
  order_expired:     { label: 'Order Expired',          icon: '⏰', color: 'text-red-500 bg-red-100',         category: 'bid' },
  order_match:       { label: 'Order Match',           icon: '🎯', color: 'text-green-600 bg-green-100',     category: 'bid' },
  bid_received:      { label: 'New Bid',              icon: '🏷️', color: 'text-blue-600 bg-blue-100',       category: 'bid' },
  bid_accepted:      { label: 'Bid Accepted',          icon: '✅', color: 'text-green-600 bg-green-100',     category: 'bid' },
  bid_rejected:      { label: 'Bid Rejected',          icon: '❌', color: 'text-red-500 bg-red-100',         category: 'bid' },
  delivery_activated:{ label: 'Delivery Active',       icon: '📦', color: 'text-orange-500 bg-orange-100',   category: 'delivery' },
  new_message:       { label: 'Message',               icon: '💬', color: 'text-indigo-600 bg-indigo-100',   category: 'message' },
  message:           { label: 'Message',               icon: '💬', color: 'text-indigo-600 bg-indigo-100',   category: 'message' },
  travel_chat_message:{ label: 'Travel Chat',          icon: '💬', color: 'text-indigo-600 bg-indigo-100',   category: 'message' },
  storage_chat_message:{ label: 'Storage Chat',        icon: '🏪', color: 'text-teal-600 bg-teal-100',       category: 'message' },
  payout_sent:       { label: 'Payout',                icon: '💰', color: 'text-emerald-600 bg-emerald-100', category: 'payment' },
  payout_failed:     { label: 'Payout Failed',         icon: '⚠️', color: 'text-red-500 bg-red-100',         category: 'payment' },
  payment_received:  { label: 'Payment',               icon: '💳', color: 'text-emerald-600 bg-emerald-100', category: 'payment' },
  escrow_released:   { label: 'Escrow Released',       icon: '🔓', color: 'text-emerald-600 bg-emerald-100', category: 'payment' },
  dispute_opened:    { label: 'Dispute',               icon: '⚡', color: 'text-amber-600 bg-amber-100',     category: 'dispute' },
  dispute_resolved:  { label: 'Dispute Resolved',      icon: '🤝', color: 'text-green-600 bg-green-100',     category: 'dispute' },
  dispute_message:   { label: 'Dispute Update',        icon: '📋', color: 'text-red-500 bg-red-100',         category: 'dispute' },
  trip_approved:     { label: 'Trip Approved',         icon: '✈️', color: 'text-green-600 bg-green-100',     category: 'delivery' },
  delivery_update:   { label: 'Delivery Update',       icon: '📦', color: 'text-orange-500 bg-orange-100',   category: 'delivery' },
  pickup_confirmed:  { label: 'Parcel Picked Up',      icon: '🚚', color: 'text-blue-600 bg-blue-100',       category: 'delivery' },
  in_transit:        { label: 'In Transit',            icon: '🛣️', color: 'text-amber-600 bg-amber-100',     category: 'delivery' },
  delivered:         { label: 'Parcel Delivered',      icon: '📬', color: 'text-green-600 bg-green-100',     category: 'delivery' },
  completed:         { label: 'Delivery Completed',    icon: '✅', color: 'text-emerald-600 bg-emerald-100', category: 'delivery' },
  payment_escrowed:  { label: 'Payment Escrowed',      icon: '🔒', color: 'text-blue-600 bg-blue-100',       category: 'payment' },
  review_pending:    { label: 'Leave a Review',        icon: '⭐', color: 'text-amber-500 bg-amber-100',     category: 'delivery' },
  review_request:    { label: 'Rate Your Traveler',    icon: '⭐', color: 'text-amber-500 bg-amber-100',     category: 'delivery' },
  moderator_action:  { label: 'Moderator Action',      icon: '🛡️', color: 'text-purple-600 bg-purple-100',   category: 'moderator' },
  verification_update:{ label: 'Verification',         icon: '🔐', color: 'text-blue-600 bg-blue-100',       category: 'moderator' },
  system:            { label: 'System',                icon: 'ℹ️', color: 'text-gray-600 bg-gray-100',       category: 'all' as FilterType },
};

function getCfg(type: string) {
  return TYPE_CONFIG[type] ?? { label: 'Notification', icon: '🔔', color: 'text-muted-foreground bg-muted', category: 'all' as FilterType };
}

function formatTimestamp(dateStr: string): string {
  return new Date(dateStr).toLocaleString('en-KE', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ${mins % 60}m ago`;
  return formatTimestamp(dateStr);
}

const FILTER_TABS: { id: FilterType; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'unread', label: 'Unread' },
  { id: 'bid', label: 'Bids' },
  { id: 'delivery', label: 'Delivery' },
  { id: 'payment', label: 'Payments' },
  { id: 'dispute', label: 'Disputes' },
  { id: 'moderator', label: 'Moderator' },
  { id: 'message', label: 'Messages' },
];

const EVENT_PREFS = [
  { key: 'bid', label: 'Bid Accepted / Received', desc: 'When bids are placed or accepted on your orders' },
  { key: 'delivery', label: 'Delivery Updates', desc: 'Status changes on active deliveries (pickup, transit, delivered)' },
  { key: 'payment', label: 'Payment Confirmations', desc: 'Escrow releases, payouts, and receipts' },
  { key: 'dispute', label: 'Dispute Updates', desc: 'New messages or decisions on disputes' },
  { key: 'moderator', label: 'Moderator Actions', desc: 'Station moderator notifications' },
  { key: 'message', label: 'New Messages', desc: 'Chat messages from senders or travelers' },
];

// Delivery status → notification mapping
const DELIVERY_STATUS_MAP: Record<string, { title: string; message: (route: string) => string }> = {
  pickup_confirmed: {
    title: '🚚 Parcel Picked Up',
    message: (route) => `Your parcel has been picked up and is on the way: ${route}.`,
  },
  in_transit: {
    title: '🛣️ Parcel In Transit',
    message: (route) => `Your parcel is now in transit: ${route}.`,
  },
  delivered: {
    title: '📬 Parcel Delivered!',
    message: (route) => `Your parcel has been delivered: ${route}. Please confirm receipt.`,
  },
  completed: {
    title: '✅ Delivery Completed',
    message: (route) => `Delivery completed and escrow released: ${route}.`,
  },
  payment_escrowed: {
    title: '🔒 Payment Escrowed',
    message: (route) => `Payment secured in escrow for delivery: ${route}.`,
  },
};

function getNotificationUrl(n: Notification): string | null {
  const id = n.reference_id;
  const type = n.type;
  const refType = n.reference_type;

  if (type === 'travel_chat_message' || type === 'new_message' || type === 'message') {
    if (id) return `/delivery-messaging?delivery=${id}`;
    return '/delivery-messaging';
  }
  if (type === 'storage_chat_message') return '/storage-provider';

  if (type === 'order_created' || type === 'order_expired' || (refType === 'order' && type !== 'bid_accepted' && type !== 'delivery_activated')) {
    return '/my-orders';
  }

  if (type === 'bid_received' || type === 'bid_rejected') {
    return '/my-bids';
  }

  // bid_accepted → go directly to the active delivery
  if (type === 'bid_accepted') {
    if (id) return `/active-deliveries?delivery=${id}`;
    return '/active-deliveries';
  }

  if (type === 'delivery_activated' || type === 'delivery_update' || refType === 'delivery') {
    if (id) return `/active-deliveries?delivery=${id}`;
    return '/active-deliveries';
  }

  if (type === 'payment_received' || type === 'escrow_released' || type === 'payout_sent' || type === 'payout_failed' || type === 'payment_escrowed') {
    return '/payments-escrow';
  }

  if (type === 'dispute_opened' || type === 'dispute_resolved' || type === 'dispute_message' || refType === 'dispute') {
    return '/disputes';
  }

  if (type === 'trip_approved' || refType === 'trip') {
    return '/my-trips';
  }

  if (type === 'verification_update') {
    return '/profile';
  }

  if (type === 'review_pending' || type === 'review_request') {
    if (id) return `/reviews?tab=pending&delivery=${id}&autoRate=1`;
    return '/reviews?tab=pending';
  }

  return null;
}

export default function NotificationsCenterContent() {
  const { user } = useAuth();
  const supabase = createClient();
  const router = useRouter();

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterType>('all');
  const [showPrefs, setShowPrefs] = useState(false);
  const [eventPrefs, setEventPrefs] = useState<Record<string, boolean>>({
    bid: true, delivery: true, payment: true, dispute: true, moderator: true, message: true,
  });
  const [prefSaved, setPrefSaved] = useState(false);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(100);
      setNotifications((data as Notification[]) || []);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) fetchNotifications();
  }, [user, fetchNotifications]);

  // ── Realtime: notifications table ──────────────────────────────────────────
  useEffect(() => {
    if (!user) return;
    const ch = supabase
      .channel(`notif-center:${user.id}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` },
        (p) => setNotifications((prev) => [p.new as Notification, ...prev]))
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` },
        (p) => setNotifications((prev) => prev.map((n) => n.id === (p.new as Notification).id ? p.new as Notification : n)))
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [user]);

  // ── Realtime: deliveries table — emit inline delivery status notifications ──
  useEffect(() => {
    if (!user) return;

    // Listen for deliveries where user is sender or traveler
    const deliveryCh = supabase
      .channel(`delivery-status:${user.id}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'deliveries' },
        (payload) => {
          const delivery = payload.new as any;
          const oldDelivery = payload.old as any;

          // Only emit if status actually changed
          if (delivery.status === oldDelivery?.status) return;

          // Only for deliveries involving this user
          if (delivery.sender_id !== user.id && delivery.traveler_id !== user.id) return;

          const milestone = DELIVERY_STATUS_MAP[delivery.status as string];
          if (!milestone) return;

          const route = delivery.route || `${delivery.pickup_city || 'Origin'} → ${delivery.destination_city || 'Destination'}`;
          const now = new Date().toISOString();

          const syntheticNotif: Notification = {
            id: `delivery-rt-${delivery.id}-${delivery.status}-${Date.now()}`,
            type: delivery.status,
            title: milestone.title,
            message: milestone.message(route),
            is_read: false,
            reference_id: delivery.id,
            reference_type: 'delivery',
            created_at: now,
          };

          setNotifications((prev) => [syntheticNotif, ...prev]);
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(deliveryCh); };
  }, [user]);

  const markRead = async (id: string) => {
    const n = notifications.find((x) => x.id === id);
    if (!n || n.is_read) return;
    // Only update DB for real (non-synthetic) notifications
    if (!id.startsWith('delivery-rt-')) {
      await supabase.from('notifications').update({ is_read: true }).eq('id', id);
    }
    setNotifications((prev) => prev.map((x) => x.id === id ? { ...x, is_read: true } : x));
  };

  const handleNotificationClick = async (n: Notification) => {
    await markRead(n.id);
    const url = getNotificationUrl(n);
    if (url) router.push(url);
  };

  const toggleRead = async (id: string) => {
    const n = notifications.find((x) => x.id === id);
    if (!n) return;
    const newState = !n.is_read;
    if (!id.startsWith('delivery-rt-')) {
      await supabase.from('notifications').update({ is_read: newState }).eq('id', id);
    }
    setNotifications((prev) => prev.map((x) => x.id === id ? { ...x, is_read: newState } : x));
  };

  const markAllRead = async () => {
    const realIds = notifications.filter((n) => !n.is_read && !n.id.startsWith('delivery-rt-')).map((n) => n.id);
    if (realIds.length) {
      await supabase.from('notifications').update({ is_read: true }).in('id', realIds);
    }
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
  };

  const filtered = notifications.filter((n) => {
    if (filter === 'unread') return !n.is_read;
    if (filter === 'all') return true;
    return getCfg(n.type).category === filter;
  });

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const savePrefs = () => {
    setPrefSaved(true);
    setTimeout(() => setPrefSaved(false), 2500);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <button
            onClick={() => router.back()}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-2"
          >
            <Icon name="ArrowLeftIcon" size={16} />
            Back
          </button>
          <h1 className="text-2xl font-bold text-foreground">Notifications</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {unreadCount > 0 ? `${unreadCount} unread notification${unreadCount !== 1 ? 's' : ''}` : 'All caught up!'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button onClick={markAllRead}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
              Mark all read
            </button>
          )}
          <button onClick={() => setShowPrefs(!showPrefs)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${showPrefs ? 'border-primary bg-primary/5 text-primary' : 'border-border text-muted-foreground hover:text-foreground hover:bg-muted'}`}>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
            Preferences
          </button>
        </div>
      </div>

      {/* Notification Preferences Panel */}
      {showPrefs && (
        <div className="bg-card border border-border rounded-2xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-foreground">Notification Preferences</h2>
            {prefSaved && <span className="text-xs text-green-600 font-medium">Saved ✓</span>}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {EVENT_PREFS.map((pref) => (
              <div key={pref.key} className="flex items-center justify-between p-3 rounded-xl border border-border">
                <div>
                  <p className="text-sm font-medium text-foreground">{pref.label}</p>
                  <p className="text-xs text-muted-foreground">{pref.desc}</p>
                </div>
                <button onClick={() => setEventPrefs((p) => ({ ...p, [pref.key]: !p[pref.key] }))}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ml-3 ${eventPrefs[pref.key] ? 'bg-primary' : 'bg-muted'}`}>
                  <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${eventPrefs[pref.key] ? 'translate-x-[18px]' : 'translate-x-0.5'}`} />
                </button>
              </div>
            ))}
          </div>
          <div className="flex justify-end">
            <button onClick={savePrefs} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors">
              Save Preferences
            </button>
          </div>
        </div>
      )}

      {/* Filter tabs */}
      <div className="flex gap-1.5 overflow-x-auto pb-1">
        {FILTER_TABS.map((tab) => {
          const count = tab.id === 'unread'
            ? unreadCount
            : tab.id === 'all'
            ? notifications.length
            : notifications.filter((n) => getCfg(n.type).category === tab.id).length;
          return (
            <button key={tab.id} onClick={() => setFilter(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all flex-shrink-0 ${filter === tab.id ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'}`}>
              {tab.label}
              {count > 0 && (
                <span className={`min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center ${filter === tab.id ? 'bg-white/20 text-white' : 'bg-background text-muted-foreground'}`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Notification list */}
      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <svg className="w-7 h-7 animate-spin text-primary/60" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <p className="text-sm text-muted-foreground">Loading notifications…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center">
              <svg className="w-7 h-7 text-muted-foreground/50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
            </div>
            <p className="text-sm font-semibold text-foreground">
              {filter === 'unread' ? 'All caught up!' : `No ${filter === 'all' ? '' : filter + ' '}notifications`}
            </p>
            <p className="text-xs text-muted-foreground">
              {filter === 'unread' ? 'You have no unread notifications.' : 'Nothing to show here yet.'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {filtered.map((n) => {
              const cfg = getCfg(n.type);
              const url = getNotificationUrl(n);
              return (
                <button
                  key={n.id}
                  onClick={() => handleNotificationClick(n)}
                  className={`w-full flex items-start gap-4 px-5 py-4 text-left transition-colors hover:bg-muted/50 ${!n.is_read ? 'bg-primary/3' : ''}`}
                >
                  {/* Icon */}
                  <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center text-lg ${cfg.color}`}>
                    {cfg.icon}
                  </div>
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className={`text-sm font-semibold text-foreground ${!n.is_read ? 'text-foreground' : 'text-muted-foreground'}`}>
                        {n.title}
                      </p>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {!n.is_read && (
                          <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                        )}
                        <span className="text-[11px] text-muted-foreground whitespace-nowrap">
                          {formatRelativeTime(n.created_at)}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.message}</p>
                    {/* Minute-precision timestamp */}
                    <p className="text-[10px] text-muted-foreground/60 mt-1">
                      {formatTimestamp(n.created_at)}
                    </p>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${cfg.color}`}>
                        {cfg.label}
                      </span>
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleRead(n.id); }}
                        className="text-[10px] text-muted-foreground hover:text-primary transition-colors underline"
                      >
                        {n.is_read ? 'Mark unread' : 'Mark read'}
                      </button>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
